import { Dayjs } from "dayjs"
import { Status } from "@/types/Status"
import {
  workstreamStatuses,
  modelNameMapping,
  manufacturerNameMapping,
} from "@/constants/smartMeters"
import { ISmartMeter } from "./smartMeter.types"

export class SmartMeter implements ISmartMeter {
  constructor(
    public deviceStatus?: string,
    public errorCode?: string,
    public errorStatus?: string,
    public installationDate?: Dayjs,
    public lastOrchestration?: string,
    public meterType?: string,
    public mpxn?: string,
    public msn?: string,
    public orchestrationStatus?: string,
    public pk?: number,
    public serviceRequest?: string,
    public latestAlert?: string,
    public latestAlertStatus?: string,
    public latestAlertDate?: Dayjs,
    public reading?: number,
    public readingDateTime?: Dayjs,
    public workstream?: string,
    public firmwareStatus?: string,
    public firmwareVersion?: string,
    public manufacturer?: string,
    public model?: string,
    public variant?: string,
    public paymentMode?: string,
    public smetsVersion?: string,
    public gbcsVersion?: string,
    public region?: string,
    public deviceId?: string,
    public ssd?: Dayjs,
    public commsHubId?: string,
  ) {}

  get isGas(): boolean {
    return this.meterType === "GSME"
  }

  get isElectricity(): boolean {
    return this.meterType === "ESME"
  }

  get latestReading(): number | null {
    return this.reading || typeof this.reading === "number"
      ? Math.trunc(this.reading)
      : null
  }

  get workstreamStatuses() {
    if (!this.workstream) return []

    return this.workstream.split(",").map((workstream) => {
      const status = workstreamStatuses[workstream]
      return status
        ? new Status(status.type, status.text)
        : new Status("normal", workstream)
    })
  }

  get installer() {
    return this.installationDate ? "So Energy" : "Another supplier"
  }

  get latestReadingFormatted() {
    // Format readings from the smart meter data to match the format from Junifer
    if (this.reading === undefined) return null

    if (this.isElectricity) {
      const kilowattValue = this.reading / 1000
      const roundedKilowattValue = Math.round(kilowattValue * 10) / 10

      return roundedKilowattValue % 1 === 0
        ? Math.trunc(roundedKilowattValue).toString()
        : roundedKilowattValue.toFixed(1)
    }

    if (this.isGas) {
      return Math.round(this.reading).toString()
    }
    return null
  }

  get modelName() {
    return this.getMappedName(this.model, modelNameMapping, "Unknown model")
  }

  get manufacturerName() {
    return this.getMappedName(
      this.manufacturer,
      manufacturerNameMapping,
      "Unknown manufacturer",
    )
  }

  get modelNameFormatted() {
    return (this.modelName ?? []).filter(Boolean).join(" - ")
  }

  get manufacturerNameFormatted() {
    return (this.manufacturerName ?? []).filter(Boolean).join(" - ")
  }

  private getMappedName(
    code: string | undefined,
    mapping: Record<string, string>,
    defaultName: string,
  ): [string, string] {
    const codeValue = code ?? ""
    const mappedName = mapping[codeValue]
    return [mappedName ?? defaultName, codeValue]
  }
}
